import { useEffect, useState } from 'react';
import MESSAGES from '../../../../common/data/constants/messages';
import { ICompany } from '../../../../common/data/dtos/company';
import Badge from '../../../../components/bootstrap/Badge';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardActions, CardBody, CardHeader, CardLabel, CardTitle } from '../../../../components/bootstrap/Card';
import { MoreActions } from '../../../../components/extras/MoreActions';
import { showSuccess, showWarning } from '../../../../components/extras/Notifications';
import PaginationButtons, { dataPagination, PER_COUNT } from '../../../../components/PaginationButtons';
import { taxIdFormat } from '../../../../helpers/helpers';
import companyService from '../../../../services/company-service';
import LoadingPage from '../../loading/LoadingPage';
import { CompanyModal } from './CompanyModal';

export const CommonCompanyList = () => {
	const [currentPage, setCurrentPage] = useState(1);
	const [perPage, setPerPage] = useState(PER_COUNT['10']);

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isModalOpened, setIsModalOpened] = useState(false);

	const [companies, setCompanies] = useState<ICompany[]>([]);
	const [selectedCompany, setSelectedCompany] = useState<ICompany | null>(null);

	useEffect(() => {
		fetchCompanies();
	}, []);

	const fetchCompanies = async () => {
		setIsLoading(true);

		const response = await companyService.getCompanies();

		setIsLoading(false);

		if (response.error) {
			showWarning(response.error);
			return;
		}

		setCompanies(response.companies || []);
	};

	const toggleModal = (isVisible: boolean) => {
		setIsModalOpened(isVisible);
	};

	const handleAddClick = () => {
		toggleModal(true);
	};

	const onModalClose = () => {
		toggleModal(false);
		setSelectedCompany(null);
	};

	const onAfterSubmit = async () => {
		await fetchCompanies();
		onModalClose();
	};

	const handleDeleteClick = async (company: ICompany) => {
		if (!company.id) {
			showWarning(MESSAGES.ACTION.DELETE_ID_ERROR);
			return;
		}

		setIsLoading(true);

		const response = await companyService.deleteCompany(company.id);

		setIsLoading(false);

		if (response.error) {
			showWarning(response.error);
			return;
		}

		showSuccess(MESSAGES.ACTION.DELETED);
		await fetchCompanies();
	};

	const handleEditClick = (company: ICompany) => {
		setSelectedCompany(company);
		toggleModal(true);
	};

	if (isLoading) {
		return <LoadingPage />;
	}

	return (
		<>
			<Card style={{ minHeight: 600 }}>
				<CardHeader borderSize={1}>
					<CardLabel icon='Store' iconColor='primary'>
						<CardTitle tag='div' className='h5'>
							Empresas
						</CardTitle>
					</CardLabel>
					<CardActions>
						<Button color='light' icon='AddCircle' onClick={handleAddClick}>
							Adicionar
						</Button>
					</CardActions>
				</CardHeader>
				<CardBody className='table-responsive'>
					<table className='table table-modern table-hover'>
						<thead>
							<tr>
								<th>CNPJ</th>
								<th>Razão Social</th>
								<th>Situação</th>
								<td aria-labelledby='Actions' />
							</tr>
						</thead>
						<tbody>
							{dataPagination(companies, currentPage, perPage).map((item: ICompany) => (
								<tr key={item.id}>
									<td>{taxIdFormat(item.taxId)}</td>
									<td>{item.corporateName}</td>
									<td>
										{item.status ? (
											<h5>
												<Badge color='success' className='align-items-center justify-content-center'>
													Ativo
												</Badge>
											</h5>
										) : (
											<h5>
												<Badge color='danger' className='align-items-center justify-content-center'>
													Inativo
												</Badge>
											</h5>
										)}
									</td>

									<MoreActions id={item.id} showEdit={true} showDelete={true} onEditClick={() => handleEditClick(item)} onDeleteClick={() => handleDeleteClick(item)} />
								</tr>
							))}
						</tbody>
					</table>
				</CardBody>
				<PaginationButtons data={companies} label='items' setCurrentPage={setCurrentPage} currentPage={currentPage} perPage={perPage} setPerPage={setPerPage} />
			</Card>

			<CompanyModal isModalOpened={isModalOpened} onModalClose={onModalClose} onAfterSubmit={onAfterSubmit} selectedCompany={selectedCompany} />
		</>
	);
};
